<script setup>
import { computed, watch } from 'vue'
import { useForm } from '@inertiajs/vue3'

// RED-355 B3: every change to the served model (similarity weights, segmentation) is an
// approved promotion. The admin names the approver, the Jira link where the approval is
// written down and the reason; without sealed evidence the rule also needs a written
// override reason, which the server asks for inline.
const props = defineProps({
  visible: { type: Boolean, default: false },
  url: { type: String, default: '' },
  title: { type: String, default: 'Kinnita ja rakenda' },
  subject: { type: String, default: '' },
})

const emit = defineEmits(['update:visible', 'promoted'])

const dialogVisible = computed({
  get: () => props.visible,
  set: value => emit('update:visible', value),
})

const form = useForm({
  approved_by: '',
  approval_link: '',
  reason: '',
  override_reason: '',
})

watch(
  () => props.visible,
  visible => {
    if (visible) {
      form.clearErrors()
    }
  },
)

// A refusal (missing fields, the evidence rule, or a run-level gate such as "worse than the
// live config") comes back as validation errors: Inertia calls onError, the dialog stays open
// with everything the admin typed, and the reason shows inline.
const submit = () => {
  form.post(props.url, {
    preserveScroll: true,
    onSuccess: () => {
      form.reset()
      dialogVisible.value = false
      emit('promoted')
    },
  })
}
</script>

<template>
  <Dialog
    v-model:visible="dialogVisible"
    modal
    :draggable="false"
    :style="{ width: '34rem' }"
    :header="title"
    data-testid="promotion-approval-dialog"
  >
    <p class="text-sm text-surface-500 mb-1">
      Serveeritava mudeli või kaalude muutmine vajab kinnitajat, kinnituse linki ja põhjust.
      Kõik jääb edutamiste logisse.
    </p>
    <p v-if="subject" class="text-sm text-surface-500 mb-4">{{ subject }}</p>
    <Message v-if="form.errors.promotion" severity="error" class="mb-4" data-testid="promotion-refused">
      {{ form.errors.promotion }}
    </Message>

    <Fluid>
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-1">
          <label for="promotion-approved-by" class="text-sm font-medium">Kinnitaja</label>
          <InputText
            id="promotion-approved-by"
            v-model="form.approved_by"
            placeholder="Toomas"
            :invalid="!!form.errors.approved_by"
            data-testid="promotion-approved-by"
          />
          <Message v-if="form.errors.approved_by" severity="error" size="small" variant="simple">
            {{ form.errors.approved_by }}
          </Message>
        </div>
        <div class="flex flex-col gap-1">
          <label for="promotion-approval-link" class="text-sm font-medium">Kinnituse link (Jira)</label>
          <InputText
            id="promotion-approval-link"
            v-model="form.approval_link"
            placeholder="https://nexbit-agency.atlassian.net/browse/RED-…"
            :invalid="!!form.errors.approval_link"
            data-testid="promotion-approval-link"
          />
          <Message v-if="form.errors.approval_link" severity="error" size="small" variant="simple">
            {{ form.errors.approval_link }}
          </Message>
        </div>
        <div class="flex flex-col gap-1">
          <label for="promotion-reason" class="text-sm font-medium">Põhjus</label>
          <Textarea
            id="promotion-reason"
            v-model="form.reason"
            rows="2"
            autoResize
            :invalid="!!form.errors.reason"
            data-testid="promotion-reason"
          />
          <Message v-if="form.errors.reason" severity="error" size="small" variant="simple">
            {{ form.errors.reason }}
          </Message>
        </div>
        <div class="flex flex-col gap-1">
          <label for="promotion-override-reason" class="text-sm font-medium">
            Erandi põhjendus (kui välise kontrolli tõend reeglit ei täida)
          </label>
          <Textarea
            id="promotion-override-reason"
            v-model="form.override_reason"
            rows="2"
            autoResize
            :invalid="!!form.errors.override_reason"
            data-testid="promotion-override-reason"
          />
          <Message v-if="form.errors.override_reason" severity="error" size="small" variant="simple">
            {{ form.errors.override_reason }}
          </Message>
        </div>
      </div>
    </Fluid>

    <template #footer>
      <Button label="Tühista" severity="secondary" text @click="dialogVisible = false" />
      <Button
        label="Kinnita ja rakenda"
        icon="pi pi-check"
        :loading="form.processing"
        data-testid="promotion-submit"
        @click="submit"
      />
    </template>
  </Dialog>
</template>
